import {
  CalendarDotsIcon,
  PaintRollerIcon,
  ChartBarIcon,
  PlugsIcon,
  CaretDownIcon,
  CurrencyCircleDollarIcon,
  CreditCardIcon,
  GearIcon,
  SignOutIcon,
  CheckIcon,
  BookIcon,
  CardsThreeIcon,
  HeadsetIcon,
  CertificateIcon,
  PaperPlaneTiltIcon
} from '@phosphor-icons/react';
import ReportsSidebar from './ReportsSidebar';
import SettingsSidebar from './SettingsSidebar';
import LogoCircle from '../../public/images/logos/logo_circle.svg';
import LogoText from '../../public/images/logos/logo_text.svg';
import { useEffect, useState, useRef, type ReactNode, Fragment } from 'react';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import Avatar from '@atoms/Avatar';
import Tooltip from '@components/Presentation/atoms/Tooltip';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@atoms/new/dropdown';
import Workspace from './Workspace';
import { useDisclosure } from '@heroui/react';
import Button from '@atoms/new/button';
import { getTeamMemberServiceSide, getUserEmailByUcc, inviteTeamMember, updateTeamMemberPublic, updateUserUcc, requestProTrial, getProTrialStatus } from '@services/youtube-platform.service';
import { notify } from '@components/Presentation/helpers';
import { getAuth, setCurrentTeamToken } from '@services/identity.service';
import { usePopupContext } from '@components/context/screenShotPupUpContext';
import ScreenShotsPopup from '@components/Screenshot-Popup';
import { useOffice } from '@components/OfficeLoader';
import { useWindowSize } from '@uidotdev/usehooks';
import { usePurchasePlan } from '@lib/hooks/usePlanSettings';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import Badge from '@atoms/Badge';
import { getFormattedPlanName } from '@components/PricingPlans/helper';

type LinkSections = {
  name: string;
  url?: string;
  icon: ReactNode;
  isExternal?: boolean;
  iconRight?: ReactNode;
  onClick?: () => void;
  className?: string;
  show?: boolean;
}

export default function Sidenav() {
  const router = useRouter();
  const pathname = usePathname();

  const { isPowerpoint } = useOffice();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    isOpen,
    onOpen,
    onClose,
    onOpenChange
  } = useDisclosure();
  const { isPopupVisible } = usePopupContext();
  const { width } = useWindowSize();

  const { doLogout, ownTeam, currentTeam, teams, selectTeam, user, currentTeamBillingAccountOwnerID, initTeam } = useTeamsData((s) => ({ doLogout: s.doLogout, ownTeam: s.ownTeam, currentTeam: s.currentTeam, teams: s.teams, selectTeam: s.selectTeam, user: s.user, currentTeamBillingAccountOwnerID: s.currentTeamBillingAccountOwnerID, initTeam: s.initTeam }));

  const [isExpanded, setIsExpanded] = useState(true);
  const [sidebarView, setSidebarView] = useState<'main' | 'reports' | 'settings'>('main');
  const [previousPath, setPreviousPath] = useState<string>('/');
  const lastMainPath = useRef<string>('/');
  const teamName = currentTeam?.billing_account?.name;

  const domains = ['@streamalive', '@cello.so'];
  const [isInternalUser, setIsInternalUser] = useState(false);
  const isEmailIncluded = (email: string) => {
    const emailDomain = email?.substring(email?.indexOf('@'));
    const check = domains?.some((domain) => emailDomain?.startsWith(domain));
    setIsInternalUser(check);
  };

  const linkSections: LinkSections[][] = [
    [
      {
        name: 'My presentations',
        url: '/presentations',
        icon: <CardsThreeIcon size={20} />,
        show: true
      },
      {
        name: 'Interaction library',
        url: '/interaction-links',
        icon: <BookIcon size={20} />,
        show: true
      },
    ],
    [
      {
        name: 'Sessions',
        url: '/sessions',
        icon: <CalendarDotsIcon size={20} />,
        show: !isPowerpoint
      },
    ],
    [
      {
        name: 'Themes',
        url: '/themes',
        icon: <PaintRollerIcon size={20} />,
        show: true
      },
      {
        name: 'Reports',
        url: '/stats',
        icon: <ChartBarIcon size={20} />,
        show: true
      },
      {
        name: 'Integrations',
        url: '/integrations',
        icon: <PlugsIcon size={20} />,
        show: !isPowerpoint
      }
    ],
  ];

  const getActiveLinkClass = (link: string, defaultClass: string, activeClass: string) => {
    if (!link) return defaultClass;
    if (pathname === '/' && link === '/') return activeClass;
    return (pathname !== '/' && link !== '/' && pathname?.startsWith(link)) ? activeClass : defaultClass;
  };

  const widthClass = isExpanded ? 'w-[300px] max-w-[300px] min-w-[300px]' : 'w-[64px] max-w-[64px] min-w-[64px]';

  useEffect(() => {
    if (!width || !pathname) return;

    if (isPowerpoint) {
      setIsExpanded(false);
      return;
    }

    if (!['/stats/', '/pricing'].some((path) => pathname.startsWith(path))) {
      setIsExpanded(true);
    }

    if (['/stats/'].some((path) => pathname.startsWith(path))) {
      setIsExpanded(true);
    }

    if (['/pricing'].some((path) => pathname.startsWith(path))) {
      if (width >= 1440) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
    }
  }, [width, pathname, isPowerpoint]);

  useEffect(() => {
    if (currentTeam?.email) isEmailIncluded(currentTeam.email);
  }, [currentTeam]);

  // Persist sidebar view based on current route
  useEffect(() => {
    const currentView = (() => {
      if (pathname?.startsWith('/stats/')) {
        return 'reports';
      } else if (pathname === '/settings') {
        return 'settings';
      } else {
        return 'main';
      }
    })();

    // Track the last main path for back navigation
    if (currentView === 'main' && pathname) {
      lastMainPath.current = pathname;
    }

    // When entering a sub-view, save the last main path as previousPath
    if (currentView !== 'main' && sidebarView === 'main') {
      setPreviousPath(lastMainPath.current);
    }

    setSidebarView(currentView);
  }, [pathname, isPowerpoint]);

  const auth = getAuth();
  const getActiveUcc = async () => {
    const { ucc } = await (window as any).Cello('getActiveUcc');

    const res = await getUserEmailByUcc(ucc);

    if (!res?.entity?.email) {
      await updateUserUcc(auth.id, ucc);
    }
  };

  const { name, settings, isFreePlan, isProTrial } = usePurchasePlan(currentTeamBillingAccountOwnerID);
  const showBanner = isFreePlan;
  const showProTrialButton = isFreePlan || isProTrial;

  const [hasRequestedTrial, setHasRequestedTrial] = useState(false);
  const [isRequestingTrial, setIsRequestingTrial] = useState(false);
  const [isApprovedTrial, setIsApprovedTrial] = useState(false);

  useEffect(() => {
    if (!currentTeamBillingAccountOwnerID) return;
    getProTrialStatus().then((res: any) => {
      if (res?.entity?.requested) setHasRequestedTrial(true);
      if (res?.entity?.approved) setIsApprovedTrial(true);
    }).catch(() => { });
  }, [currentTeamBillingAccountOwnerID]);

  const handleRequestProTrial = async () => {
    if (hasRequestedTrial || isRequestingTrial) return;
    setIsRequestingTrial(true);
    try {
      await requestProTrial();
      setHasRequestedTrial(true);
      notify('Thanks! We\'ve received your request and will review it shortly. You\'ll hear from us via email soon.', 'success');
    } catch {
      notify('Something went wrong. Please try again.', 'error');
    } finally {
      setIsRequestingTrial(false);
    }
  };

  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams?.get('team')) {
      setIsDropdownOpen(true);
    }
  }, [searchParams]);

  const fetchAndAcceptInvite = async (id: string) => {
    try {
      const result = await getTeamMemberServiceSide(
        id,
        id
      );
      const teamInfo = result.entity;

      if (!user) return;

      const payload = { accepted: true, user_id: user.id };
      const response = await updateTeamMemberPublic(
        teamInfo.id,
        teamInfo.billing_account.id,
        payload
      );

      if (response.status && response.entity.token) {
        await setCurrentTeamToken(response.entity.token);
        await inviteTeamMember();
        router.replace('/presentations');
      }
    } catch (err) {
      console.error('Error processing team invite:', err);
    }
  };

  useEffect(() => {
    if (!searchParams?.get('invite') || !searchParams?.get('id')) return;
    fetchAndAcceptInvite(searchParams.get('id')!);
    initTeam();
  }, [user, searchParams]);

  return (
    <>
      <header className={`${widthClass} bg-content2 pb-4 flex flex-col h-screen overflow-auto relative border-r border-divider`}>

        {sidebarView === 'main' && (
          <div>
            <div className='flex gap-2 h-16 items-center'>
              {isExpanded && (
                <>
                  <LogoText className="ml-9 text-grey-900 dark:text-white" />
                  {isPowerpoint && <Badge variant="warning" >Beta</Badge>}
                </>
              )}

              {!isExpanded && <LogoCircle className="mx-auto" />}
            </div>

            <div className={`${isExpanded ? 'px-6' : ''} border-t border-b border-divider `}>
              <Dropdown
                placement='bottom-start'
                isOpen={isDropdownOpen}
                className='w-[300px]'
                onClose={() => {
                  setIsDropdownOpen(false);
                }}>
                <DropdownTrigger>
                  <button
                    className={`aria-expanded:scale-[1] w-full focus-visible:outline-none cursor-pointer my-1 rounded-lg ${isExpanded && 'hover:bg-content3 active:bg-content3'}`}
                    onClick={() => {
                      setIsDropdownOpen(prev => !prev);
                    }}>
                    <div className={`${isExpanded ? 'px-2' : 'px-2'} py-1 flex items-center`}>
                      <div className='flex-1 overflow-hidden'>
                        <div className='font-medium text-sm flex gap-2 items-center justify-center'>
                          <Avatar
                            w='8'
                            h='8'
                            isProfile
                            name={teamName} />
                          <div className={`${isExpanded ? 'flex flex-col flex-wrap flex-1 text-left truncate' : 'hidden'}`}>
                            <span className='truncate w-full'>{isExpanded && teamName && teamName}</span>
                            <div className='flex'>
                              <Badge className='uppercase'>
                                {getFormattedPlanName(name).split('(')[0]}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                      {isExpanded && <div className="cursor-pointer">
                        <CaretDownIcon size={16} />
                      </div>}
                    </div>
                  </button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Static Actions"
                  disallowEmptySelection
                  selectionMode="single"
                  selectedKeys={[currentTeam?.id || '']}
                >
                  <DropdownSection showDivider
                    className="m-0!"
                    classNames={{
                      divider: 'mt-1 mb-2',
                    }}>
                    <DropdownItem className='data-[hover=true]:bg-transparent! data-[hover=true]:cursor-default'>
                      <p className='text-sm font-normal text-default-500'>
                        {currentTeam?.email}
                      </p>

                      {/* {settings?.credits && (
                    <div className='flex items-center gap-2 mt-1'>
                      <p className='text-sm font-normal text-default-700'>{settings?.credits} AI Credits</p>
                      {!['Enterprise Plan'].includes(name) && (
                        <Tooltip
                          placement='right'
                          content="We are introducing AI credits. While they're still in beta, enjoy unlimited credits temporarily!">
                          <HelpIcon className="text-default-400" fontSize="small" />
                        </Tooltip>
                      )}
                    </div>
                  )} */}
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection showDivider
                    className="m-0!"
                    classNames={{
                      divider: 'mt-1 mb-2',
                    }}>
                    <>
                      {!isPowerpoint && (
                        <DropdownItem
                          key="refer-a-friend"
                          startContent={<CurrencyCircleDollarIcon size={20} />}
                          onAction={getActiveUcc}
                          className={'celloClass'}
                        >
                          <div className='flex items-center justify-between w-full'>
                            <span>Refer a friend</span>
                            <Badge>Earn $100</Badge>
                          </div>
                          <div className='announcement'></div>
                        </DropdownItem>
                      )}
                      <DropdownItem
                        key="pricing"
                        startContent={<CreditCardIcon size={20} />}
                        onAction={() => {
                          router.push('/pricing');
                          setIsDropdownOpen(false);
                        }}
                      >
                        Billing and plan
                      </DropdownItem>
                      <DropdownItem
                        key="settings"
                        startContent={<GearIcon size={20} />}
                        onAction={() => {
                          router.push('/settings');
                          setIsDropdownOpen(false);
                        }}
                      >
                        Settings
                      </DropdownItem>
                    </>
                  </DropdownSection>
                  <DropdownSection title='WORKSPACE' id='workspace-section'
                    className="m-0!"
                    classNames={{
                      group: 'max-h-32 overflow-auto border-b border-divider py-1',
                      heading: 'sticky top-0 bg-content2 z-10',
                    }}>
                    {teams.map((team) => (
                      <DropdownItem
                        className='h-12'
                        endContent={currentTeam?.id === team.id ?
                          <Tooltip
                            content='Workspace settings'
                          >
                            <Button
                              kind='icon-only'
                              size='sm'
                              className='relative right-0 '
                            >
                              <GearIcon size={20}
                                onClick={() => {
                                  onOpen();
                                  setIsDropdownOpen(false);
                                }}
                              />
                            </Button>
                          </Tooltip> : null}
                        startContent={currentTeam?.id === team.id ? <CheckIcon size={20} /> : <div className='w-5' />}
                        selectedIcon={() => null}
                        key={team.id}
                        onAction={() => {
                          if (team.id === currentTeam?.id) return;
                          selectTeam(team);
                          setIsDropdownOpen(false);
                          router.push('/');
                        }}
                      >
                        <div className='flex items-start flex-col w-full'>
                          <span className='truncate'>{team.billing_account.name}</span>
                          {team.billing_account.purchase_plan?.name && (
                            <Badge className='uppercase'>
                              {getFormattedPlanName(team.billing_account.purchase_plan.name).split('(')[0]}
                            </Badge>
                          )}
                        </div>
                      </DropdownItem>

                    ))}
                  </DropdownSection>
                  <DropdownItem
                    key="delete"
                    onAction={async () => {
                      await doLogout();
                      router.push('/login');
                    }}
                  >
                    <p className='text-red-500 flex gap-2'>
                      <SignOutIcon size={20} /> Log out
                    </p>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        )}

        {sidebarView === 'main' && (
          <div className={`${isExpanded ? 'px-6' : 'flex flex-col items-center'} mt-6 text-sm`}>
            {linkSections.map((section, index) => (
              <div key={index} className={'flex flex-col gap-2 mb-2'}>
                {section.map((link, linkIndex) => {
                  if (!link.show) return null;
                  return (
                    <Fragment key={linkIndex}>
                      <Tooltip key={link.name}
                        content={link.name}
                        placement='left'
                        isDisabled={isExpanded}
                      >
                        <Link
                          href={link.url || '#'}
                          onClick={link?.onClick || (() => { })}
                          className={`rounded-md flex items-center px-2 py-1.5 h-8 gap-4 ${getActiveLinkClass(link?.url || '', 'text-default-700 hover:bg-content3', 'bg-primary-500/20 text-primary-500')}`}
                          target={link?.isExternal ? '_blank' : undefined}
                        >
                          <div className={`${link?.className}  w-5 h-5 flex items-center`}>
                            {link.icon}
                          </div>
                          {isExpanded && <span className='text-sm'>{link.name}</span>}
                          {isExpanded && link.iconRight && <div className='ml-auto flex'>{link.iconRight}</div>}
                        </Link>
                      </Tooltip>
                    </Fragment>
                  );
                })}
                {index !== linkSections.length - 1 && linkSections[index]?.some(link => link.show) && (
                  <div className='h-px bg-divider my-2 mx-2' />
                )}
              </div>
            ))}
          </div>
        )}

        {sidebarView === 'main' && isExpanded && (
          <div className='px-6 mt-auto text-sm'>
            <div className='h-px bg-divider my-4 mx-2' />
            <div className='flex flex-col gap-2 mb-2'>
              {showProTrialButton && (
                hasRequestedTrial ? (
                  <Tooltip
                    isDisabled={isApprovedTrial}
                    content="Your request has been sent. We'll get back to you soon." placement="top">
                    <div className='rounded-md flex items-center px-2 py-1.5 h-8 gap-4 text-default-400 cursor-not-allowed opacity-60'>
                      <div className='w-5 h-5 flex items-center'>
                        <PaperPlaneTiltIcon size={20} />
                      </div>
                      <span className='text-sm'>Get Pro trial</span>
                      <span className='ml-auto text-sm'>2 weeks free</span>
                    </div>
                  </Tooltip>
                ) : (
                  <div
                    onClick={handleRequestProTrial}
                    className={`rounded-md flex items-center px-2 py-1.5 h-8 gap-4 text-default-700 hover:bg-content3 ${isRequestingTrial ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    <div className='w-5 h-5 flex items-center'>
                      <PaperPlaneTiltIcon size={20} />
                    </div>
                    <span className='text-sm font-normal'>Get Pro trial</span>
                    <span className='ml-auto text-default-500 text-sm font-normal'>2 weeks free</span>
                  </div>
                )
              )}
              <Link
                href='https://tidycal.com/team/streamalive/here-to-help'
                target='_blank'
                className='rounded-md flex items-center px-2 py-1.5 h-8 gap-4 text-default-700 hover:bg-content3'
              >
                <div className='w-5 h-5 flex items-center'>
                  <HeadsetIcon size={20} />
                </div>
                <span className='text-sm'>Confused?</span>
                <span className='ml-auto text-default-500 text-sm'>Book a call</span>
              </Link>
              <Link
                href='https://tidycal.com/team/streamalive/human-engagement-specialist'
                target='_blank'
                className='rounded-md flex items-center px-2 py-1.5 h-8 gap-4 text-default-700 hover:bg-content3'
              >
                <div className='w-5 h-5 flex items-center'>
                  <CertificateIcon size={20} />
                </div>
                <span className='text-sm'>Get certified</span>
                <Badge className='ml-auto uppercase'>free</Badge>
              </Link>
            </div>
            {showBanner && <div className='h-px bg-divider my-4 mx-2' />}
          </div>
        )}

        {sidebarView === 'reports' && (
          <ReportsSidebar
            isExpanded={isExpanded}
            onBack={() => {
              router.push('/stats');
            }}
          />
        )}

        {sidebarView === 'settings' && (
          <SettingsSidebar
            isExpanded={isExpanded}
            onBack={() => {
              router.push(previousPath);
            }}
          />
        )}

        {sidebarView === 'main' && showBanner && isExpanded && (<div className=' mx-6 py-4 px-3 bg-content3 rounded-lg'>
          <div className='flex items-center gap-2'>
            {/* <Badge variant='primary'>Engage</Badge> */}
            <h4 className='text-sm font-medium leading-5'>Upgrade to Starter</h4>
          </div>
          <p className='text-xs text-default-700 my-2'>Unlock more participants and enable branding.</p>
          <Link href='/pricing' className='text-blue-600 text-sm font-semibold'>View plans</Link>
        </div>)
        }
        {isOpen && (
          <Workspace
            isOpen={isOpen}
            onClose={onClose}
            onOpenChange={onOpenChange}
          />
        )}
      </header >

      {isPopupVisible && !isPowerpoint &&
        <ScreenShotsPopup />
      }
    </>
  );
}
